import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import AdminShell from "../../components/AdminShell";
import { Card, Button } from "../../components/ui-kit";
import { Warning, CheckCircle, MapPin, Phone } from "@phosphor-icons/react";
import { connectSocket } from "../../lib/socket";

function timeAgo(iso) {
  if (!iso) return "—";
  const diff = Math.max(0, Date.now() - new Date(iso).getTime());
  const m = Math.floor(diff / 60000);
  if (m < 1) return "à l'instant";
  if (m < 60) return `il y a ${m} min`;
  const h = Math.floor(m / 60);
  if (h < 24) return `il y a ${h} h`;
  return new Date(iso).toLocaleString("fr-FR");
}

export default function AdminSOS() {
  const [incidents, setIncidents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("OPEN");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/incidents");
      setIncidents(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    load();
    let cleanup = null;
    (async () => {
      const socket = await connectSocket();
      if (!socket) return;
      socket.connect();
      const onNew = (inc) => {
        toast.error(`🚨 SOS de ${inc?.user_name || "?"}`, { duration: 8000 });
        load();
      };
      socket.on("sos.new", onNew);
      cleanup = () => socket.off("sos.new", onNew);
    })();
    return () => { if (cleanup) cleanup(); };
  }, [load]);

  const resolve = async (id) => {
    try {
      await api.post(`/admin/incidents/${id}/resolve`);
      toast.success("Incident résolu");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const filtered = incidents.filter(i => filter === "ALL" ? true : i.status === filter);
  const openCount = incidents.filter(i => i.status === "OPEN").length;

  return (
    <AdminShell title="SOS">
      <div className="flex items-center justify-between">
        <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tighter flex items-center gap-3" data-testid="sos-title">
          <Warning weight="fill" className="text-destructive" /> Incidents SOS
        </h1>
        <div className="rounded-full glass-strong px-4 py-2 text-xs font-bold uppercase tracking-widest">
          <span className="text-destructive">{openCount}</span> ouvert · {incidents.length} total
        </div>
      </div>

      <div className="mt-6 flex gap-2" data-testid="sos-filters">
        {["OPEN", "RESOLVED", "ALL"].map(s => (
          <button
            key={s}
            onClick={() => setFilter(s)}
            className={`rounded-full px-4 py-2 text-[11px] font-bold uppercase tracking-widest ${
              filter === s ? "bg-primary text-primary-foreground" : "glass-strong text-muted-foreground"
            }`}
            data-testid={`filter-${s.toLowerCase()}`}
          >{s === "OPEN" ? "Ouverts" : s === "RESOLVED" ? "Résolus" : "Tous"}</button>
        ))}
      </div>

      <div className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-2">
        {loading && <div className="text-sm font-bold uppercase tracking-widest">Chargement…</div>}
        {filtered.map(inc => (
          <Card key={inc.id} className="p-5" data-testid={`incident-${inc.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1">
                <div className="text-[10px] font-bold uppercase tracking-widest text-destructive">
                  {inc.status === "OPEN" ? "● Ouvert" : "Résolu"} · {inc.user_role}
                </div>
                <h3 className="font-heading text-xl font-black">{inc.user_name}</h3>
                <div className="flex items-center gap-2 font-mono text-xs text-muted-foreground">
                  <Phone size={12} /> {inc.user_phone}
                </div>
                <div className="mt-2 text-[10px] text-muted-foreground">{timeAgo(inc.created_at)}</div>
                {inc.note && <div className="mt-2 rounded-lg bg-white/5 p-2 text-xs">{inc.note}</div>}
                {(inc.lat && inc.lng) && (
                  <a
                    href={`https://www.openstreetmap.org/?mlat=${inc.lat}&mlon=${inc.lng}#map=17/${inc.lat}/${inc.lng}`}
                    target="_blank" rel="noreferrer"
                    className="mt-2 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest text-primary"
                    data-testid={`map-${inc.id}`}
                  >
                    <MapPin size={12} /> Voir sur la carte ({inc.lat.toFixed(4)}, {inc.lng.toFixed(4)})
                  </a>
                )}
                {inc.ride_id && (
                  <div className="mt-1 font-mono text-[10px] text-muted-foreground">
                    Course : #{inc.ride_id.slice(0, 8).toUpperCase()}
                  </div>
                )}
              </div>
            </div>
            {inc.status === "OPEN" && (
              <div className="mt-4 flex gap-2">
                <Button variant="secondary" className="flex-1" onClick={() => resolve(inc.id)} data-testid={`resolve-${inc.id}`}>
                  <CheckCircle weight="fill" /> Marquer résolu
                </Button>
                <a
                  href={`tel:${inc.user_phone}`}
                  className="pill border border-white/15 bg-white/5 px-4 py-3 text-sm font-bold inline-flex items-center gap-2"
                  data-testid={`call-${inc.id}`}
                >
                  <Phone weight="fill" /> Appeler
                </a>
              </div>
            )}
          </Card>
        ))}
        {!loading && filtered.length === 0 && (
          <Card className="p-8 text-center text-sm font-bold uppercase tracking-widest text-muted-foreground" data-testid="sos-empty">
            Aucun incident
          </Card>
        )}
      </div>
    </AdminShell>
  );
}
